import { useState, useEffect } from "react";
import { RedactionDetail } from "../types/shared.types";

interface AnalyticsData {
  overview: {
    total_jobs: number;
    total_redactions: number;
    total_processing_time: number;
    average_redactions_per_job: number;
    average_processing_time_per_job: number;
  };
  model_comparison: Array<{
    model_name: string;
    total_jobs: number;
    total_redactions: number;
    average_processing_time: number;
    average_confidence: number;
    redactions_per_second: number;
    file_type_distribution: Record<string, number>;
    redaction_type_distribution: Record<string, number>;
    recent_performance: Array<{
      timestamp: number;
      processing_time: number;
      redactions: number;
      confidence: number;
      file_size: number;
    }>;
  }>;
  document_analysis: Array<{
    file_type: string;
    redaction_type: string;
    total_files: number;
    total_redactions: number;
    average_confidence: number;
    average_processing_time: number;
    complexity_score: number;
  }>;
  recent_jobs: Array<{
    model_choice: string;
    redaction_type: string;
    file_extension: string;
    total_redactions: number;
    processing_time: number;
    average_confidence: number;
    file_size: number;
    timestamp: number;
  }>;
  entity_frequency: RedactionDetail[];
}

export const useAnalytics = (refreshTrigger: number) => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const analyticsUrl =
    process.env.NODE_ENV === "production"
      ? "https://ner-api.pobl.tech/api/analytics"
      : "http://localhost:8000/api/analytics";

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(analyticsUrl);
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        setError(`Failed to fetch analytics: ${response.statusText}`);
      }
    } catch (error) {
      setError(
        `Failed to fetch analytics: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const resetAnalytics = async () => {
    try {
      const response = await fetch(`${analyticsUrl}/reset`, {
        method: "POST",
      });
      if (response.ok) {
        setAnalyticsData(null);
        setError(null);
      } else {
        setError("Failed to reset analytics");
      }
    } catch (error) {
      setError(
        `Failed to reset analytics: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  };

  useEffect(() => {
    fetchAnalytics();

    // Refresh analytics every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [refreshTrigger]);

  return {
    analyticsData,
    isLoading,
    error,
    fetchAnalytics,
    resetAnalytics,
  };
};
